import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "../assets/images/commaLogo.png";
import { Avatar } from "@mui/material";
import { Mic, Add, ArrowBack } from "@mui/icons-material";
import { LogOut } from "lucide-react";
import dayjs from "dayjs";

const API_BASE_URL = "https://genz-staging.feeltiptop.com";

const PatientProfile = ({ patientId, onBack, doctorInformation }) => {
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doctorInfo, setDoctorInfo] = useState(doctorInformation);

  const handleBackToList = () => {
    onBack();
  };

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      "Content-Type": "application/json",
    },
  });

  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/api/patient/${patientId}`,
          getAuthHeaders(),
        );
        console.log("API Response:", response.data);
        if (response.data.success) {
          setPatient(response.data.patient);
          // Transform the assessments data to include feedId
          const transformedAssessments = response.data.assessments.map(
            (assessment) => {
              console.log("Raw Assessment:", assessment);
              return {
                ...assessment,
                feedId: assessment.feedId || assessment.id, // Try to use feedId first, fallback to id
                date: assessment.date,
                type: assessment.type || "Standard Assessment",
                assetCount: assessment.assetCount || 0,
              };
            },
          );
          console.log("Transformed Assessments:", transformedAssessments);
          setAssessments(transformedAssessments);
        }
      } catch (err) {
        setError("Failed to fetch patient data");
        console.error("Error fetching patient data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatientData();
  }, [patientId]);

  const handleNewAssessment = () => {
    navigate("/CommaFeedConstruction", {
      state: {
        patientId: patient.patientId,
        patientName: patient.fullName,
      },
    });
  };

  const handleNewSpeechAsset = () => {
    navigate("/assetcreate", {
      state: {
        patientId: patient.patientId,
        patientName: patient.fullName,
      },
    });
  };

  const handleViewReport = (assessment) => {
    console.log("Viewing report for assessment:", assessment);
    navigate(`/ReportPage`, {
      state: {
        feedId: assessment.feedId,
        userId: patient.userId,
        userName: patient.fullName,
        feedName: assessment.type,
        feedDate: assessment.date,
      },
    });
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div
          className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="p-6">
        <div
          className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">Patient not found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Navbar */}
      <nav className="flex items-center justify-between bg-white px-6 py-4 shadow">
        <div
          className="group relative flex items-center"
          style={{ minHeight: 80 }}
        >
          {/* Logo and Title */}
          <div
            className="duration-400 flex items-center transition-all ease-in-out"
            style={{}}
          >
            {/* Animated Back Button */}
            <button
              onClick={handleBackToList}
              className="duration-400 pointer-events-none z-10 -translate-x-14 transform transition-all ease-in-out group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100"
            >
              <ArrowBack className="h-6 w-6 text-gray-600" />
            </button>
            <button onClick={handleBackToList}>
              <img
                src={Logo}
                alt="COMMA logo"
                className="w-85 duration-400 h-20 transition-all ease-in-out group-hover:ml-5"
                style={{
                  transition: "margin-left 0.4s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </button>
            <div className="duration-400 group-hover:mHei4 ml-4 text-2xl font-semibold text-gray-800 transition-all ease-in-out">
              Patient Profile
            </div>
          </div>
        </div>
        {doctorInfo && (
          <div className="flex items-center space-x-4">
            <Avatar
              src={`https://api.dicebear.com/7.x/initials/svg?seed=${doctorInfo.firstName + " " + doctorInfo.lastName}`}
              alt={doctorInfo.firstName + " " + doctorInfo.lastName}
              sx={{ width: 50, height: 50 }} // MUI uses pixels
            />
            <span className="text-lg font-bold text-gray-700">
              Dr. {doctorInfo?.firstName} {doctorInfo?.lastName}
            </span>
            <button onClick={handleLogout}>
              <LogOut className="h-6 w-6 text-gray-600" />
            </button>
            {/* Animated Back Button */}
            <button
              onClick={handleBackToList}
              className="duration-400 pointer-events-none z-10 -translate-x-14 transform opacity-0 transition-all"
            >
              <ArrowBack className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        )}
      </nav>

      <main className="p-6">
        {/* Patient Info */}
        <div className="mb-4 flex items-center space-x-4">
          <Avatar
            src={`https://api.dicebear.com/7.x/initials/svg?seed=${patient.fullName}`}
            alt={patient.fullName}
            sx={{ width: 96, height: 96 }} // MUI uses pixels
          />
          <div className="text-left">
            <h1 className="text-3xl font-semibold text-gray-800">
              {patient.fullName}
            </h1>
            <p className="text-lg font-semibold text-gray-600">
              #{String(patient.patientId).padStart(5, "0")} • Age {patient.age}{" "}
              • {patient.gender}
            </p>
          </div>
        </div>
        {/* Action Cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div
            onClick={handleNewAssessment}
            className="flex cursor-pointer items-center space-x-2 rounded-lg bg-white p-4 shadow hover:shadow-md"
          >
            <Add className="text-2xl text-gray-500" />
            <div>
              <h2 className="text-lg font-medium text-gray-800">
                New Speech Assessment
              </h2>
              <p className="text-sm text-gray-500">
                Create a new assessment record
              </p>
            </div>
          </div>
          <div
            onClick={handleNewSpeechAsset}
            className="flex cursor-pointer items-center space-x-2 rounded-lg bg-white p-4 shadow hover:shadow-md"
          >
            <Mic className="text-2xl text-gray-500" />
            <div>
              <h2 className="text-lg font-medium text-gray-800">
                New Speech Asset
              </h2>
              <p className="text-sm text-gray-500">
                Upload or record new speech data
              </p>
            </div>
          </div>
        </div>
        {/* Assessment History Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Assets
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {assessments.map((assessment) => (
                <tr key={assessment.id}>
                  <td className="px-6 py-4 text-left text-gray-700">
                    {dayjs(assessment.date).format("MMM D, YYYY")}
                  </td>
                  <td className="px-6 py-4 text-left text-gray-700">
                    <span className="inline-block rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                      {assessment.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-left text-gray-700">
                    {assessment.assetCount} audio files
                  </td>
                  <td className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleViewReport(assessment)}
                      className="text-[#2A6EC2] hover:underline"
                    >
                      View Report
                    </button>
                  </td>
                </tr>
              ))}
              {assessments.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No assessments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default PatientProfile;
